import React, { useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Search, AlertTriangle, RefreshCw, KeyRound,
  Power, Mail, CheckCircle2, XCircle,
} from "lucide-react";

export default function AuthRepairPage() {
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [bulkResetting, setBulkResetting] = useState(false);

  const [lookupEmail, setLookupEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const runAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get("/admin/auth/audit");
      setAudit(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const runRepair = useCallback(async () => {
    if (!window.confirm(
      "Run Auth Repair?\n\n• Lowercase + trim every email\n• Default missing active_status to true\n• Deduplicate users with the same email (winner = active + highest role)\n• Drop orphan OAuth sessions\n\nThis is idempotent and audit-logged."
    )) return;
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/auth/repair", { confirm: "REPAIR AUTH" });
      toast.success(`Repair complete — deduplicated ${data.deleted_loser_users} accounts, normalized ${data.normalized_emails} emails`);
      runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  }, [runAudit]);

  const runLookup = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const em = (lookupEmail || "").trim();
    if (!em) return toast.error("Enter an email");
    setLookupLoading(true);
    try {
      const { data } = await api.get(`/admin/auth/lookup`, { params: { email: em } });
      setLookup(data);
      if (!data.match_count) toast.warning("No accounts match that email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setLookupLoading(false);
    }
  }, [lookupEmail]);

  const sendResetEmail = useCallback(async (uid, fullName) => {
    if (!window.confirm(`Email a password reset link to ${fullName}?`)) return;
    try {
      const { data } = await api.post(`/users/${uid}/reset-password`);
      if (data.email_sent) toast.success(`Reset email sent to ${data.to}`);
      else toast.warning("Token created — but email not sent. Check Resend config.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset email");
    }
  }, []);

  const clearSessions = useCallback(async (uid, fullName) => {
    if (!window.confirm(`Force-logout ${fullName} from every browser?\nTheir current OAuth cookie sessions will be wiped.`)) return;
    try {
      const { data } = await api.post(`/admin/auth/clear-sessions`, { user_id: uid });
      toast.success(`Cleared ${data.sessions_deleted} session(s) for ${data.affected_user}`);
      runLookup();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clear sessions");
    }
  }, [runLookup]);

  const runBulkReset = useCallback(async (forceChange) => {
    const phrase = window.prompt(
      `Bulk-reset every active user's password to "123455"?${forceChange ? " They will be forced to set a new password on next login." : ""}\n\nType the phrase below to confirm:\n\nRESET PASSWORDS`
    );
    if (phrase !== "RESET PASSWORDS") {
      if (phrase != null) toast.error("Phrase did not match. Aborted.");
      return;
    }
    setBulkResetting(true);
    try {
      const { data } = await api.post("/admin/bulk-reset-password",
        { confirm: "RESET PASSWORDS", new_password: "123455", force_change: !!forceChange });
      toast.success(data.message || "Reset complete");
      runAudit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk reset failed");
    } finally {
      setBulkResetting(false);
    }
  }, [runAudit]);

  if (!user) return null;
  const allowed = ["CEO", "Project Manager"].includes(user.access_role);
  if (!allowed) {
    return (
      <div className="p-6 sm:p-8">
        <div className="nv-card p-6 max-w-2xl">
          <div className="flex items-center gap-2 text-rose-700 font-semibold">
            <AlertTriangle size={18} /> Access denied
          </div>
          <p className="text-sm text-slate-600 mt-2">Only CEO and Project Manager can access Auth Repair tools.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-5xl">
      <div>
        <p className="text-small-caps">Admin · Settings</p>
        <h1 className="text-h1 mt-1">Auth Repair</h1>
        <p className="text-sm text-slate-500 mt-1">Diagnose and fix login problems. Every action is audit-logged.</p>
      </div>

      {/* === Audit === */}
      <section className="nv-card p-6 space-y-4" data-testid="auth-audit-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
            <ShieldCheck size={16} className="text-sky-700" /> System Audit
          </div>
          <button
            onClick={runAudit}
            disabled={auditLoading}
            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-sky-700 hover:bg-sky-800 text-white disabled:opacity-60"
            data-testid="run-audit-btn"
          >
            {auditLoading ? "Running…" : "Run audit"}
          </button>
        </div>
        {audit ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="audit-results">
            <Stat label="Total users" value={audit.total_users} />
            <Stat label="Active users" value={audit.active_users} good={audit.active_users === audit.total_users} />
            <Stat label="Duplicate emails" value={audit.duplicate_emails_count} bad={audit.duplicate_emails_count > 0} />
            <Stat label="Need normalization" value={audit.needs_email_normalization_count} bad={audit.needs_email_normalization_count > 0} />
            <Stat label="No password hash" value={audit.users_without_password_hash.length} bad={audit.users_without_password_hash.length > 0} />
            <Stat label="No active_status" value={audit.users_without_active_status.length} bad={audit.users_without_active_status.length > 0} />
            <Stat label="Total sessions" value={audit.total_sessions} />
            <Stat label="Orphan sessions" value={audit.orphan_sessions_count} bad={audit.orphan_sessions_count > 0} />
          </div>
        ) : (
          <p className="text-sm text-slate-500">Click <em>Run audit</em> to scan user records.</p>
        )}

        {audit && (() => {
          const hasIssues = audit.duplicate_emails_count > 0
            || audit.needs_email_normalization_count > 0
            || audit.orphan_sessions_count > 0
            || audit.users_without_active_status.length > 0;
          return (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              {hasIssues && (
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-semibold text-slate-800">Issues detected — running Auth Repair will fix them.</p>
                    <ul className="list-disc ml-5 text-slate-600 mt-1 space-y-0.5">
                      {audit.duplicate_emails_count > 0 && <li>{audit.duplicate_emails_count} duplicate email group(s) — losers will be deleted, scores reassigned</li>}
                      {audit.needs_email_normalization_count > 0 && <li>{audit.needs_email_normalization_count} email(s) need lowercase/trim</li>}
                      {audit.orphan_sessions_count > 0 && <li>{audit.orphan_sessions_count} orphan OAuth session(s)</li>}
                      {audit.users_without_active_status.length > 0 && <li>{audit.users_without_active_status.length} user(s) missing active_status — defaulting to true</li>}
                    </ul>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-slate-500">
                  {hasIssues
                    ? "Run Auth Repair to fix the issues above."
                    : "No issues detected. You can still run Auth Repair as a safety check — it's idempotent."}
                </p>
                <button
                  onClick={runRepair}
                  disabled={repairing}
                  className={`px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-60 ${
                    hasIssues ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-600 hover:bg-slate-700"
                  }`}
                  data-testid="run-repair-btn"
                >
                  {repairing ? "Repairing…" : "Run Auth Repair"}
                </button>
              </div>
            </div>
          );
        })()}
      </section>

      {/* === User Lookup === */}
      <section className="nv-card p-6 space-y-4" data-testid="auth-lookup-section">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
          <Search size={16} className="text-sky-700" /> User Lookup
        </div>
        <form onSubmit={runLookup} className="flex gap-2 flex-wrap">
          <input
            type="email"
            placeholder="user@example.com"
            value={lookupEmail}
            onChange={(e) => setLookupEmail(e.target.value)}
            className="flex-1 min-w-[220px] text-sm rounded-md border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
            data-testid="lookup-email-input"
          />
          <button
            type="submit"
            disabled={lookupLoading}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-sky-700 hover:bg-sky-800 text-white disabled:opacity-60"
            data-testid="lookup-btn"
          >
            {lookupLoading ? "Looking up…" : "Look up"}
          </button>
        </form>
        {lookup && (
          <div data-testid="lookup-results" className="space-y-3">
            {lookup.matches.length === 0 ? (
              <p className="text-sm text-slate-500">No accounts match <strong>{lookup.email_searched}</strong>.</p>
            ) : lookup.matches.map((m) => (
              <div key={m.user_id} className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold text-slate-800">{m.full_name}</p>
                    <p className="text-slate-500">{m.email} · <span className="text-slate-700">{m.access_role}</span></p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Pill ok={m.active_status} okText="Active" badText="Inactive" />
                    <Pill ok={m.has_password_hash} okText="Password set" badText="No password" />
                    {m.must_change_password && <Pill warn warnText="Must change pw" />}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-600">
                  <KV k="User ID" v={m.user_id} />
                  <KV k="Home" v={m.assigned_home || (m.assigned_homes || []).join(", ") || "—"} />
                  <KV k="Active sessions" v={String(m.active_sessions)} />
                  <KV k="Password changed" v={m.password_changed_at ? new Date(m.password_changed_at).toLocaleString() : "—"} />
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    onClick={() => sendResetEmail(m.user_id, m.full_name)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-sky-700 hover:bg-sky-800 text-white"
                    data-testid={`send-reset-${m.user_id}`}
                  >
                    <Mail size={12} /> Send reset email
                  </button>
                  <button
                    onClick={() => clearSessions(m.user_id, m.full_name)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700 hover:bg-slate-800 text-white"
                    data-testid={`clear-sessions-${m.user_id}`}
                  >
                    <Power size={12} /> Clear sessions
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* === Bulk Reset === */}
      <section className="nv-card p-6 space-y-4 border-rose-200" data-testid="auth-bulkreset-section">
        <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm">
          <RefreshCw size={16} /> Bulk Password Reset
        </div>
        <p className="text-sm text-slate-600">
          Reset every <strong>active</strong> user's password to <code className="bg-slate-100 px-1 rounded">123455</code> and wipe all OAuth sessions. Use sparingly.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => runBulkReset(false)}
            disabled={bulkResetting}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-60"
            data-testid="bulk-reset-btn"
          >
            <KeyRound size={14} className="inline mr-1.5" />
            Reset all → 123455
          </button>
          <button
            onClick={() => runBulkReset(true)}
            disabled={bulkResetting}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-900 hover:bg-rose-950 text-white disabled:opacity-60"
            data-testid="bulk-reset-force-btn"
          >
            <KeyRound size={14} className="inline mr-1.5" />
            Reset all → 123455 + force change
          </button>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, good, bad }) {
  const tone = bad ? "text-rose-700 bg-rose-50 border-rose-200"
              : good ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={`rounded-md border px-3 py-2.5 ${tone}`}>
      <p className="text-[11.5px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="text-lg font-bold mt-0.5">{value}</p>
    </div>
  );
}

function Pill({ ok, okText, badText, warn, warnText }) {
  if (warn) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-amber-100 text-amber-800">
        <AlertTriangle size={10} /> {warnText}
      </span>
    );
  }
  return ok ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-emerald-100 text-emerald-800">
      <CheckCircle2 size={10} /> {okText}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-rose-100 text-rose-800">
      <XCircle size={10} /> {badText}
    </span>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <span className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-500">{k}</span>
      <span className="ml-1.5">{v}</span>
    </div>
  );
}
